import { trpc } from "@/api/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@tanstack/react-router";
import { AlertCircle, Calendar, CheckCircle2, MapPin } from "lucide-react";

interface ConfirmationPageProps {
  token: string;
}

const DATE_FMT = new Intl.DateTimeFormat("cs-CZ", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

export function ConfirmationPage({ token }: ConfirmationPageProps) {
  const query = trpc.hca.prihlasky.getPrihlaskaByToken.useQuery({ token }, { retry: false });

  if (query.isError) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="hca-potvrzeni-error">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" aria-hidden="true" />
        <h1 className="mb-2 text-2xl font-bold">Potvrzení nelze načíst</h1>
        <p className="mb-6 text-muted-foreground">{query.error.message}</p>
        <Button variant="outline" asChild data-testid="hca-potvrzeni-error-back-button">
          <Link to="/terminy" data-testid="hca-potvrzeni-error-back">
            Zpět na termíny
          </Link>
        </Button>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16" data-testid="hca-potvrzeni-skeleton">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="mt-4 h-40 w-full" />
      </div>
    );
  }

  const data = query.data;

  return (
    <div className="mx-auto max-w-2xl px-4 py-16" data-testid="hca-potvrzeni-page">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
        <CheckCircle2 className="h-7 w-7" aria-hidden="true" />
      </div>
      <h1 className="mb-2 text-3xl font-bold tracking-tight">Děkujeme, {data.klient.jmeno}!</h1>
      <p className="mb-8 text-muted-foreground">
        Vaše přihláška byla úspěšně přijata. Brzy se vám ozveme s potvrzením a dalšími detaily na{" "}
        <strong>{data.klient.email}</strong>.
      </p>

      <Card className="mb-6" data-testid="hca-potvrzeni-termin-card">
        <CardHeader>
          <CardTitle className="text-xl">{data.termin.productNazev}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {DATE_FMT.format(new Date(data.termin.datumOd))}
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {data.termin.misto}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3 rounded-lg bg-muted/30 p-5 text-sm">
        <h2 className="font-semibold">Co bude dál?</h2>
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
          <li>Náš tým vás zkontaktuje s potvrzením a detaily semináře.</li>
          <li>Před termínem vám dáme vědět čas zahájení i praktické informace k místu konání.</li>
          <li>
            Fakturu vám vystavíme po potvrzení účasti. Splatnost se řídí kalkulací dle lhůty do
            začátku semináře.
          </li>
          <li>Pokud potřebujete přihlášku zrušit, napište nám prosím co nejdříve.</li>
        </ul>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Button
          variant="outline"
          className="w-full flex-1"
          asChild
          data-testid="hca-potvrzeni-terminy-button"
        >
          <Link to="/terminy" data-testid="hca-potvrzeni-terminy-link">
            Další termíny
          </Link>
        </Button>
        <Button className="w-full flex-1" asChild data-testid="hca-potvrzeni-home-button">
          <Link to="/" data-testid="hca-potvrzeni-home-link">
            Zpět na úvod
          </Link>
        </Button>
      </div>
    </div>
  );
}
